/**
 * ModeIndicator Component (Issue #302)
 *
 * The current chat mode, as a chip that opens the mode picker.
 */
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { useAppDispatch, useAppSelector } from '@store/index';
import {
  selectActiveModes,
  selectCurrentMode,
  selectCurrentModeConfig,
  selectModeSwitching,
  selectShowModeSelector,
  setCurrentMode,
  setShowModeSelector,
  switchMode,
} from '@features/chat/chatModeSlice';
import { ChatMode } from '../../types/chat';
import { getModeIcon } from '../../constants/chatModes';
import ModeSelector from './ModeSelector';
import { cn } from '../../lib/utils';

interface ModeIndicatorProps {
  /** Persists the switch against a thread; without one it is session-local. */
  threadId?: string;
}

const ModeIndicator: React.FC<ModeIndicatorProps> = ({ threadId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const currentMode = useAppSelector(selectCurrentModeConfig);
  const currentModeId = useAppSelector(selectCurrentMode);
  const modes = useAppSelector(selectActiveModes);
  const isSwitching = useAppSelector(selectModeSwitching);
  // Open state lives in Redux because other things open this — the
  // retry-in-another-mode link on an answer, for one.
  const isOpen = useAppSelector(selectShowModeSelector);

  const handleSelect = useCallback(
    (mode: ChatMode) => {
      if (mode.id !== currentModeId) {
        if (threadId) {
          dispatch(switchMode({ threadId, request: { mode_id: mode.id } }));
        } else {
          dispatch(setCurrentMode(mode.id));
        }
      }
      dispatch(setShowModeSelector(false));
    },
    [currentModeId, dispatch, threadId],
  );

  if (!currentMode) return null;

  const Icon = getModeIcon(currentMode.icon);
  const modeName = t(`chat.modes.${currentMode.id}.name`, currentMode.name);
  const modeDescription = t(
    `chat.modes.${currentMode.id}.description`,
    currentMode.description || '',
  );

  return (
    <DropdownMenu open={isOpen} onOpenChange={(open) => dispatch(setShowModeSelector(open))}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={isSwitching}
          title={modeDescription || modeName}
          // The visible label leads, so it is contained in the accessible name
          // (WCAG 2.5.3); the MUI Tooltip this replaced overwrote it entirely.
          aria-label={modeDescription ? `${modeName}: ${modeDescription}` : modeName}
          // Each mode carries its own colour as data, so it arrives as a custom
          // property rather than a class Tailwind would need to know up front.
          style={{ ['--mode-color' as string]: currentMode.color }}
          className={cn(
            'inline-flex h-7 items-center gap-1 rounded-full border px-2 text-xs font-medium transition-colors',
            'border-[var(--mode-color)] bg-[color-mix(in_srgb,var(--mode-color)_12%,transparent)]',
            'hover:bg-[color-mix(in_srgb,var(--mode-color)_22%,transparent)]',
            'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:pointer-events-none disabled:opacity-50',
            // The mode colours are picked for a white background. Used as-is on
            // the dark surface the label lands near 3:1, so it is lightened there.
            'text-[var(--mode-color)] dark:text-[color-mix(in_srgb,var(--mode-color)_55%,white)]',
            'dark:border-[color-mix(in_srgb,var(--mode-color)_70%,white)]',
          )}
        >
          <Icon className="size-3.5" strokeWidth={2} />
          {modeName}
          <ChevronDown
            className={cn('size-3.5 transition-transform', isOpen && 'rotate-180')}
            strokeWidth={2}
          />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="center" side="top" sideOffset={8} className="revy-ui w-[320px] p-0">
        <ModeSelector
          modes={modes}
          currentModeId={currentModeId}
          isSwitching={isSwitching}
          onSelect={handleSelect}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ModeIndicator;
